import { PageDefaultSegmentWithHeader } from "@sis/react-library";
import { Track } from "../../../interfaces/dtos/track"
import { RPMTable } from "../../shared/table/RPMTable";
import { ConfigurationsListHeader } from "./ConfigurationsListHeader";
import { TableBody, useTheme } from "@mui/material";
import { ConfigurationsListItem } from "./ConfigurationsListItem";
import { ConfigurationsListPagination } from "./ConfigurationsListPagination";
import { useEffect, useState } from "react";
import { ConfigurationsListActionsLeft, FilterChangeParams } from "./ConfigurationsListActionsLeft";
import { useSearchParams } from "react-router-dom";
import { segmentHeaderDefault } from "../../../utils/commonUtils";

interface ConfigurationsListProps {
    tracks: Track[];
    readOnly: boolean;
}

export const ConfigurationsList = ({
    tracks, readOnly
}: ConfigurationsListProps) => {

    const theme = useTheme();

    const [page, setPage] = useState(1);
    const [searchParams] = useSearchParams();

    const [search, setSearch] = useState("");
    const [filterCountry, setFilterCountry] = useState(" ");
    const [filterStatus, setFilterStatus] = useState(" ");

    useEffect(() => {
        // If selection changes reset back to page 1
        // eslint-disable-next-line  
        setPage(1);
    }, [searchParams, search, filterCountry, filterStatus])


    const pageSize = 50;

    const paginate = () => {
        const pageCount = Math.ceil(filteredTracks.length / pageSize);
        return Array.from({ length: pageCount }, (_, index) =>
            filteredTracks.slice(index * pageSize, (index + 1) * pageSize)
        );
    };

    const matchesFilter = (track: Track) => {
        if (!matchesSearchItem(track)) {
            return false;
        }
        if (filterCountry !== " " && track.countryCode !== filterCountry) {
            return false;
        }
        if (
            filterStatus !== " " && 
            (filterStatus === "A" && !track.isTrackActive) || 
            (filterStatus === "I" && track.isTrackActive)) {
            return false;
        }
        return true;
    }

    const matchesSearchItem = (track: Track) => {
        if (search === "") { return true; }
        const searchFields: (keyof Track)[] = ['sportCode', 'shortDescription', 'longDescription', 'countryCode'];
        return searchFields.some((field) => {
            // Disabled lint for any below, any used to dynamically get field from list
            // eslint-disable-next-line
            const itemValue = (track as any)[field].toString().toLowerCase();
            return itemValue.indexOf(search.toLowerCase()) > -1;
        })
    }

    const handleChangePage = (newPage: number) => {
        setPage(newPage);
    };

    const handleFilterChange: FilterChangeParams = (filterType, filterValue) => {
        switch (filterType) {
            case "search": setSearch(filterValue); break;
            case "country": setFilterCountry(filterValue); break;
            case "status": setFilterStatus(filterValue); break;
        }
    }

    const filteredTracks = tracks.filter((t) => matchesFilter(t));
    const needsPagination = filteredTracks.length > pageSize;
    const paginatedItems = paginate();
    const currentItems = paginatedItems[page - 1] ?? [];

    return (
        <PageDefaultSegmentWithHeader
            headingElement={
                <ConfigurationsListActionsLeft
                    tracks={tracks}
                    search={search}
                    filterCountry={filterCountry}
                    filterStatus={filterStatus}
                    onFilterChange={handleFilterChange}
                />}
            headingBackground={segmentHeaderDefault(theme)}
            headingColor={theme.palette.common.white}
        >
            <RPMTable
                containerProps={{ sx: { height: `calc(100vh - ${needsPagination ? '295' : '260'}px)` } }}
                tableProps={{ stickyHeader: true, id: 'configurations-list' }}
            >
                <ConfigurationsListHeader />
                <TableBody>
                    {currentItems.map((track, i) =>
                        <ConfigurationsListItem
                            key={`${track.key}${i}`}
                            track={track}
                            readOnly={readOnly}
                        />
                    )}
                </TableBody>
            </RPMTable>
            <ConfigurationsListPagination
                show={needsPagination}
                itemCount={filteredTracks.length}
                pageSize={pageSize}
                page={page}
                onPageChange={handleChangePage}
            />
        </PageDefaultSegmentWithHeader >
    )
}
